/**
 * SubscriptionPlansView.js
 * System Admin catalog maintenance for subscription bundles (plans).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icons } from '../common/Icons';
import { subscriptionPlansAdminService } from '../../services/api';

const INCLUDE_LABELS = [
  { key: 'includes_pos', label: 'POS' },
  { key: 'includes_restaurant_pos', label: 'Restaurant POS' },
  { key: 'includes_ecommerce', label: 'eCommerce' },
  { key: 'includes_herds_flocks', label: 'Herds & Flocks' },
  { key: 'includes_market_manager', label: 'Market Manager' },
  { key: 'includes_accounting', label: 'Accounting' },
  { key: 'includes_reports', label: 'Reports' },
  { key: 'includes_api_access', label: 'API access' },
];

function centsToDollars(cents) {
  if (cents == null) return '';
  return (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
}

function dollarsToCents(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function highlightsToText(features) {
  if (!features) return '';
  const list = Array.isArray(features)
    ? features
    : Array.isArray(features.highlights)
      ? features.highlights
      : [];
  return list.join('\n');
}

function emptyForm() {
  return {
    name: '',
    slug: '',
    description: '',
    price_monthly_dollars: '',
    price_yearly_dollars: '',
    tier_level: '',
    sort_order: '100',
    max_users: '5',
    max_locations: '1',
    max_products: '100',
    max_monthly_orders: '500',
    is_active: true,
    is_featured: false,
    highlights_text: '',
    includes_pos: true,
    includes_restaurant_pos: false,
    includes_ecommerce: false,
    includes_herds_flocks: false,
    includes_accounting: true,
    includes_reports: true,
    includes_api_access: false,
    includes_market_manager: false,
  };
}

function planToForm(plan) {
  return {
    name: plan.name || '',
    slug: plan.slug || '',
    description: plan.description || '',
    price_monthly_dollars: centsToDollars(plan.price_monthly),
    price_yearly_dollars: centsToDollars(plan.price_yearly),
    tier_level: plan.tier_level != null ? String(plan.tier_level) : '',
    sort_order: plan.sort_order != null ? String(plan.sort_order) : '0',
    max_users: plan.max_users != null ? String(plan.max_users) : '5',
    max_locations: plan.max_locations != null ? String(plan.max_locations) : '1',
    max_products: plan.max_products != null ? String(plan.max_products) : '100',
    max_monthly_orders: plan.max_monthly_orders != null ? String(plan.max_monthly_orders) : '500',
    is_active: !!plan.is_active,
    is_featured: !!plan.is_featured,
    highlights_text: highlightsToText(plan.features),
    includes_pos: !!plan.includes_pos,
    includes_restaurant_pos: !!plan.includes_restaurant_pos,
    includes_ecommerce: !!plan.includes_ecommerce,
    includes_herds_flocks: !!plan.includes_herds_flocks,
    includes_accounting: !!plan.includes_accounting,
    includes_reports: !!plan.includes_reports,
    includes_api_access: !!plan.includes_api_access,
    includes_market_manager: !!plan.includes_market_manager,
  };
}

function formToPayload(form) {
  const priceMonthly = dollarsToCents(form.price_monthly_dollars);
  const priceYearly = dollarsToCents(form.price_yearly_dollars);
  if (priceMonthly == null) {
    throw new Error('Monthly price is required');
  }
  if (priceYearly == null) {
    throw new Error('Yearly price is required');
  }
  const tier = parseInt(form.tier_level, 10);
  if (!Number.isInteger(tier) || tier < 1) {
    throw new Error('Tier level must be a positive integer bitmask');
  }

  return {
    name: form.name.trim(),
    slug: form.slug.trim() || undefined,
    description: form.description.trim() || null,
    price_monthly: priceMonthly,
    price_yearly: priceYearly,
    tier_level: tier,
    sort_order: parseInt(form.sort_order, 10) || 0,
    max_users: parseInt(form.max_users, 10),
    max_locations: parseInt(form.max_locations, 10),
    max_products: parseInt(form.max_products, 10),
    max_monthly_orders: parseInt(form.max_monthly_orders, 10),
    is_active: !!form.is_active,
    is_featured: !!form.is_featured,
    features: {
      highlights: form.highlights_text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    },
    includes_pos: !!form.includes_pos,
    includes_restaurant_pos: !!form.includes_restaurant_pos,
    includes_ecommerce: !!form.includes_ecommerce,
    includes_herds_flocks: !!form.includes_herds_flocks,
    includes_accounting: !!form.includes_accounting,
    includes_reports: !!form.includes_reports,
    includes_api_access: !!form.includes_api_access,
    includes_market_manager: !!form.includes_market_manager,
  };
}

function PlanEditor({ title, form, setForm, onSave, onCancel, saving, error }) {
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 className="card-title" style={{ margin: 0 }}>{title}</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          <Icons.X size={14} /> Cancel
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Name</label>
          <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Slug</label>
          <input
            className="form-control"
            value={form.slug}
            onChange={(e) => set('slug', e.target.value)}
            placeholder="auto from name if blank on create"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          rows={2}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Monthly price (USD)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              value={form.price_monthly_dollars}
              onChange={(e) => set('price_monthly_dollars', e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Yearly price (USD)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              value={form.price_yearly_dollars}
              onChange={(e) => set('price_yearly_dollars', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Tier level (bitmask)</label>
          <input
            type="number"
            min="1"
            className="form-control"
            value={form.tier_level}
            onChange={(e) => set('tier_level', e.target.value)}
          />
          <p className="form-hint">Controls App Registry access bits. Copy from a similar plan if unsure.</p>
        </div>
        <div className="form-group">
          <label className="form-label">Sort order</label>
          <input
            type="number"
            className="form-control"
            value={form.sort_order}
            onChange={(e) => set('sort_order', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        {['max_users', 'max_locations', 'max_products', 'max_monthly_orders'].map((key) => (
          <div className="form-group" key={key}>
            <label className="form-label">{key.replace(/_/g, ' ')} (-1 = unlimited)</label>
            <input
              type="number"
              className="form-control"
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
            />
          </div>
        ))}
      </div>

      <div className="form-group">
        <label className="form-label">Feature highlights (one per line)</label>
        <textarea
          className="form-control"
          rows={4}
          value={form.highlights_text}
          onChange={(e) => set('highlights_text', e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
        {INCLUDE_LABELS.map(({ key, label }) => (
          <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={!!form[key]}
              onChange={(e) => set(key, e.target.checked)}
            />
            {label}
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
          />
          Active (offered to new tenants)
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!form.is_featured}
            onChange={(e) => set('is_featured', e.target.checked)}
          />
          Featured (most popular)
        </label>
      </div>

      <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
        {saving ? <Icons.Loader size={16} className="spin" /> : <Icons.Check size={16} />}
        {saving ? 'Saving…' : 'Save plan'}
      </button>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default function SubscriptionPlansView() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showRetired, setShowRetired] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subscriptionPlansAdminService.list({ include_retired: true });
      setPlans(res.data?.plans || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (showRetired ? plans : plans.filter((p) => p.is_active)),
    [plans, showRetired]
  );

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setNotice(null);
  };

  const startEdit = (plan) => {
    setCreating(false);
    setEditingId(plan.id);
    setForm(planToForm(plan));
    setFormError(null);
    setNotice(null);
  };

  const cancelEditor = () => {
    setCreating(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    setNotice(null);
    try {
      const payload = formToPayload(form);
      if (creating) {
        const res = await subscriptionPlansAdminService.create(payload);
        setNotice(res.message || 'Plan created');
      } else if (editingId) {
        const res = await subscriptionPlansAdminService.update(editingId, payload);
        setNotice(res.message || 'Plan updated');
      }
      cancelEditor();
      await load();
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleRetired = async (plan) => {
    const nextActive = !plan.is_active;
    const verb = nextActive ? 'reactivate' : 'retire';
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} “${plan.name}”?`)) return;
    setNotice(null);
    try {
      const res = await subscriptionPlansAdminService.update(plan.id, { is_active: nextActive });
      setNotice(res.message || `Plan ${verb}d`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${verb} plan`);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={40} className="spin" />
        <p>Loading subscription plans…</p>
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>
            Create, reprice, feature, and retire subscription bundles offered during onboarding and on the Tenant Subscription tab.
            Price changes apply to <strong>new</strong> subscriptions; existing Stripe subscribers keep their contracted price.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>
          <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
            <Icons.RefreshCw size={14} /> Refresh
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={startCreate}>
            <Icons.Plus size={14} /> New plan
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success mb-3" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Icons.CheckCircle size={16} />
          <span>{notice}</span>
        </div>
      )}

      {(creating || editingId) && (
        <PlanEditor
          title={creating ? 'Create subscription plan' : 'Edit subscription plan'}
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancelEditor}
          saving={saving}
          error={formError}
        />
      )}

      <div className="card">
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Plan</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Monthly</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Yearly</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Tier</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Tenants</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Status</th>
              <th style={{ padding: '10px 8px', fontSize: 13 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((plan) => (
              <tr key={plan.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '12px 8px' }}>
                  <div style={{ fontWeight: 600 }}>
                    {plan.name}
                    {plan.is_featured ? (
                      <span className="badge badge-neutral" style={{ marginLeft: 8 }}>Featured</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{plan.slug}</div>
                </td>
                <td style={{ padding: '12px 8px' }}>${centsToDollars(plan.price_monthly)}</td>
                <td style={{ padding: '12px 8px' }}>${centsToDollars(plan.price_yearly)}</td>
                <td style={{ padding: '12px 8px' }}>{plan.tier_level}</td>
                <td style={{ padding: '12px 8px' }}>{plan.tenant_count ?? 0}</td>
                <td style={{ padding: '12px 8px' }}>
                  <span className={`badge badge-${plan.is_active ? 'success' : 'neutral'}`}>
                    {plan.is_active ? 'Active' : 'Retired'}
                  </span>
                </td>
                <td style={{ padding: '12px 8px' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => startEdit(plan)}>
                      <Icons.Edit size={14} /> Edit
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleRetired(plan)}>
                      {plan.is_active ? 'Retire' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <p style={{ color: 'var(--text-muted)', padding: 16 }}>No plans to show.</p>
        )}
      </div>
    </div>
  );
}
